import {
  RA_BAND_KEYS,
  RA_MATRIX_MAX_BANDS,
  RA_MATRIX_MIN_BANDS,
  type RaMatrixBand,
  type RaMatrixDefinition,
} from '@felix/domain';

/**
 * Settings ▸ Risk Matrix bant düzenleyicisinin saf modeli (ADR 0085 §2). Kişi her bandın üst
 * sınırını seçer; sonraki bandın alt sınırı onu izler, ilk bant 1'den başlar, son bant 25'te
 * biter. Böylece düzenleyici boşluk ve çakışma üretmez; kuralları yine domain denetler
 * (`raMatrixIssues`) ve sunucu yayımda reddeder.
 */

const clone = (definition: RaMatrixDefinition): RaMatrixBand[] =>
  definition.bands.map((band) => ({ ...band, acceptedBy: [...band.acceptedBy] }));

/** Bandın üst sınırını değiştirir; sonraki bandın alt sınırı bir fazlası olur. */
export function withMax(
  definition: RaMatrixDefinition,
  index: number,
  max: number,
): RaMatrixDefinition {
  const bands = clone(definition);
  const band = bands[index];
  if (!band || index === bands.length - 1) return definition;
  band.max = max;
  const next = bands[index + 1];
  if (next) next.min = max + 1;
  return { bands };
}

/** Bandın adını, kabul seviyesini ya da kabul edenlerini değiştirir. */
export function withBand(
  definition: RaMatrixDefinition,
  index: number,
  patch: Partial<Pick<RaMatrixBand, 'key' | 'acceptance' | 'acceptedBy'>>,
): RaMatrixDefinition {
  const bands = clone(definition);
  const band = bands[index];
  if (!band) return definition;
  bands[index] = {
    ...band,
    ...patch,
    acceptedBy:
      (patch.acceptance ?? band.acceptance) === 'unacceptable'
        ? []
        : [...(patch.acceptedBy ?? band.acceptedBy)],
  };
  return { bands };
}

/**
 * Son bandın önüne yeni bant ekler: son bandın aralığı ikiye bölünür, ad iki bandın arasındaki ilk
 * boş addır (yoksa sona yeni ad). Bölünecek aralık ya da boş ad yoksa tanım aynen döner.
 */
export function addBand(definition: RaMatrixDefinition): RaMatrixDefinition {
  const bands = clone(definition);
  if (bands.length >= RA_MATRIX_MAX_BANDS) return definition;
  const last = bands.at(-1);
  const before = bands.at(-2);
  if (!last || last.max - last.min < 1) return definition;
  const lastIndex = RA_BAND_KEYS.indexOf(last.key);
  const beforeIndex = before ? RA_BAND_KEYS.indexOf(before.key) : -1;
  const middle = Math.floor((last.min + last.max) / 2);
  const between = RA_BAND_KEYS.slice(beforeIndex + 1, lastIndex)[0];
  if (between) {
    const band: RaMatrixBand = {
      key: between,
      min: last.min,
      max: middle,
      acceptance: before?.acceptance ?? 'acceptable',
      acceptedBy: [...(before?.acceptedBy ?? [])],
    };
    last.min = middle + 1;
    bands.splice(bands.length - 1, 0, band);
    return { bands };
  }
  const after = RA_BAND_KEYS[lastIndex + 1];
  if (!after) return definition;
  bands.push({
    key: after,
    min: middle + 1,
    max: last.max,
    acceptance: last.acceptance,
    acceptedBy: [...last.acceptedBy],
  });
  last.max = middle;
  return { bands };
}

/** Bandı kaldırır; aralığını sonraki bant (son bantsa önceki) alır. En az iki bant kalır. */
export function removeBand(definition: RaMatrixDefinition, index: number): RaMatrixDefinition {
  const bands = clone(definition);
  const removed = bands[index];
  if (!removed || bands.length <= RA_MATRIX_MIN_BANDS) return definition;
  bands.splice(index, 1);
  const next = bands[index];
  if (next) next.min = removed.min;
  else {
    const previous = bands[index - 1];
    if (previous) previous.max = removed.max;
  }
  return { bands };
}

/** İki tanım aynı mı (yayım düğmesi değişiklik yoksa kapalı). */
export function sameDefinition(a: RaMatrixDefinition, b: RaMatrixDefinition): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
